import { ProcessingLevel } from "@redotech/crdb-enums/fulfillment/processing-level";
import { locationIdFormat } from "@redotech/redo-inventory-id";
import { unaryRpc } from "@redotech/rpc/definition";
import * as z from "zod";
import {
  inventoryLaneDraftSchema,
  inventoryLaneSchema,
  inventoryRuleSaveResultSchema,
} from "./inventory-rule-models";

const laneScope = {
  locationId: locationIdFormat.schema,
  processingLevel: z.enum(ProcessingLevel),
};

/**
 * Overrides by rank, the level's fixed rows (empty until saved), legacy grade rows
 * that still have live rules, both additional item rows (verification and grading), then the backstop.
 */
export const listInventoryLanes = unaryRpc({
  name: "listInventoryLanes" as const,
  input: z.object(laneScope),
  output: z.object({ lanes: z.array(inventoryLaneSchema) }),
});

/** Writes one rule per step action and rewrites the exclusions of the lanes ranked below. */
export const saveInventoryLane = unaryRpc({
  name: "saveInventoryLane" as const,
  input: inventoryLaneDraftSchema,
  output: inventoryRuleSaveResultSchema.extend({
    lanes: z.array(inventoryLaneSchema),
  }),
});

/** Re-ranks the overrides and rewrites the exclusions they imply. */
export const reorderInventoryLanes = unaryRpc({
  name: "reorderInventoryLanes" as const,
  input: z.object({
    ...laneScope,
    /** Every override's lane key, in the new rank order. */
    laneKeys: z.array(z.string().min(1)).min(1),
  }),
  output: z.object({ lanes: z.array(inventoryLaneSchema) }),
});

/** Disables the lane's rules and removes its exclusions from the lanes below. */
export const deleteInventoryLane = unaryRpc({
  name: "deleteInventoryLane" as const,
  input: z.object({ ...laneScope, laneKey: z.string().min(1) }),
  output: z.object({ lanes: z.array(inventoryLaneSchema) }),
});
